/**
 * Defines all parametrized data sources available in API. Data sources are used for simple
 * scoped data used on frontend, closely coupled with the block definitions and slots. The
 * data sources can be also used for other applications.
 */
export enum DataSourceType {
    /**
     * A data source returning set of heating system types for specific device like
     * for `GAS_BOILER` we will get a possible subtypes of that boiler, e.g., `CONDENSING_BOILER`.
     * This result is a merge of C-API result and scopes.
     * @type {DataSourceType.HeatingSystemTypes}
     */
    HeatingSystemTypes = 'HEATING_SYSTEM_TYPES',
    /**
     * A data source returning set of heat generator types like `GAS_BOILER` or `WOOD_STOVE`.
     * This result is a merge of C-API result and scopes.
     * @type {DataSourceType.HeatGeneratorTypes}
     */
    HeatGeneratorTypes = 'HEAT_GENERATOR_TYPES',
    /**
     * A data source returning a set of energy sources for specific heat generator, e.g., a `WOOD_STOVE`
     * may have 3 energy sources `PELLET`, `SOFTWOOD` and `HARDWOOD`. This result is a merge of C-API
     * result and scopes.
     * @type {DataSourceType.HeatGeneratorEnergySources}
     */
    HeatGeneratorEnergySources = 'HEAT_GENERATOR_ENERGY_SOURCES',
    /**
     * A data source returning set of roof types. This result is a merge of C-API result and scopes.
     * @type {DataSourceType.RoofTypes}
     */
    RoofTypes = 'ROOF_TYPES',
    /**
     * A data source returning set of building types. This result is a merge of C-API result and scopes.
     * @type {DataSourceType.BuildingTypes}
     */
    BuildingTypes = 'BUILDING_TYPES',
    /**
     * A data source returning set of building placements (locations in the building area).
     * Determines if a building is a freestanding or maybe a part of a terraced house.
     *  This result is a merge of C-API result and scopes.
     * @type {DataSourceType.BuildingPlacements}
     */
    BuildingPlacements = 'BUILDING_PLACEMENTS',
    /**
     * A data source returning set of consumption profiles. This result is a merge of C-API result and scopes.
     * @type {DataSourceType.ConsumptionProfiles}
     */
    ConsumptionProfiles = 'CONSUMPTION_PROFILES',
    /**
     * A data source returning set of heat pump operating modes. This result is a merge of C-API result and scopes.
     * @type {DataSourceType.HeatPumpOperatingModes}
     */
    HeatPumpOperatingModes = 'HEAT_PUMP_OPERATING_MODES',
    /**
     * A data source returning set of window types. This result is a merge of C-API result and scopes.
     * @type {DataSourceType.WindowTypes}
     */
    WindowTypes = 'WINDOW_TYPES',
    /**
     * Represents a data source containing only 2 values 'YES' & 'NO' used for data-source-based
     * questions with Yes/NO, True/False answers.
     * @type {DataSourceType.YesNoValues}
     */
    YesNoValues = 'YES_NO_VALUES'
}
